import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CalendarClock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Responsive schedule-follow-up sheet. Bottom-sheet on mobile, centred dialog
 * on desktop. Works for either a trip (tripId) or a lead (leadId).
 */
const FollowUpSheet = ({ open, onClose, tripId, leadId, prefillDate, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(prefillDate || today);
  const [time, setTime] = useState('10:00');
  const [assignTo, setAssignTo] = useState('');
  const [notes, setNotes] = useState('');
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(prefillDate || today);
    axios.get(`${BACKEND_URL}/api/followups/agents`, { withCredentials: true })
      .then((r) => setAgents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAgents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillDate]);

  if (!open) return null;

  const save = async () => {
    if (!date) { toast.error('Pick a date'); return; }
    setSaving(true);
    try {
      const body = { trip_id: tripId || null, lead_id: leadId || null, scheduled_date: date, scheduled_time: time, notes };
      if (assignTo) body.assigned_to = assignTo;
      const r = await axios.post(`${BACKEND_URL}/api/followups`, body, { withCredentials: true });
      toast.success('Follow-up scheduled');
      onSaved?.(r.data);
      onClose?.();
      setNotes('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not schedule follow-up');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center" data-testid="followup-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-xl animate-in slide-in-from-bottom-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-teal-700" /> Schedule Follow-up
          </h3>
          <button type="button" onClick={onClose} data-testid="followup-sheet-close" className="h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="followup-date"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500 mb-1 block">Time</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="followup-time"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Assign to</label>
            <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="followup-assignee"
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Me (default)</option>
              {agents.map((a) => (
                <option key={a.user_id} value={a.user_id}>{a.name}{a.role === 'admin' ? ' (admin)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-1 block">Notes (private to assignee &amp; admin)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="followup-notes"
              placeholder="What's the follow-up about?" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none" />
          </div>
        </div>

        <button type="button" onClick={save} disabled={saving} data-testid="followup-save-btn"
          className="mt-4 w-full h-12 rounded-xl bg-teal-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
          {saving ? 'Scheduling…' : 'Schedule Follow-up'}
        </button>
      </div>
    </div>
  );
};

export default FollowUpSheet;
